import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Share2, X, Copy, Check, ChevronDown } from 'lucide-react';
import SmartCalculatorsSidebar from '../../components/common/SmartCalculatorsSidebar';
import Navbar from '../../components/common/Navbar';

const TAX_FREE_LIMIT = 2000000; // ₹20 lakh

const GratuityCalculator = () => {
  const [salaryStr, setSalaryStr]   = useState('50000');
  const [years, setYears]           = useState(10);
  const [coveredAct, setCoveredAct] = useState(true);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied]         = useState(false);
  const [openFaq, setOpenFaq]       = useState(null);

  const salary = parseInt(salaryStr) || 0;
  const sliderSalary = Math.min(Math.max(salary, 5000), 500000);

  const { gratuity, taxFree, taxable } = useMemo(() => {
    if (salary <= 0 || years <= 0) return { gratuity: 0, taxFree: 0, taxable: 0 };
    const g = coveredAct
      ? Math.round((salary * 15 * years) / 26)
      : Math.round((salary * 15 * years) / 30);
    const tf = Math.min(g, TAX_FREE_LIMIT);
    return { gratuity: g, taxFree: tf, taxable: Math.max(g - tf, 0) };
  }, [salary, years, coveredAct]);

  const fmt = (n) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

  const fmtC = (n) => {
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
    if (n >= 100000)   return `₹${(n / 100000).toFixed(2)}L`;
    return fmt(n);
  };

  const sliderBg = (val, min, max) => {
    const pct = ((val - min) / (max - min)) * 100;
    return `linear-gradient(to right, #115e59 0%, #115e59 ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`;
  };

  const handleShare = (platform) => {
    const text = encodeURIComponent('Calculate your gratuity amount with Kapitalised: kapitalised.com/calculators/gratuity');
    if (platform === 'copy') {
      navigator.clipboard.writeText('https://kapitalised.com/calculators/gratuity');
      setCopied(true); setTimeout(() => setCopied(false), 2000); return;
    }
    const maps = { whatsapp: `https://wa.me/?text=${text}`, twitter: `https://twitter.com/intent/tweet?text=${text}` };
    window.open(maps[platform], '_blank', 'width=600,height=400');
    setShowShareMenu(false);
  };

  const labelSt    = { fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 500, color: '#374151' };
  const valueSt    = { fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 600, color: '#115e59', border: 'none', outline: 'none', background: 'transparent', textAlign: 'right' };
  const suffixSt   = { fontFamily: 'Onest, sans-serif', fontSize: '15px', fontWeight: 500, color: '#9ca3af' };
  const minMaxSt   = { fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af' };
  const inputBoxSt = { display: 'flex', alignItems: 'center', gap: '3px', backgroundColor: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '6px 12px' };

  // Donut chart: tax-free vs taxable
  const R = 70, CX = 100, CY = 100;
  const circ    = 2 * Math.PI * R;
  const gap     = 5;
  const tfPct   = gratuity > 0 ? taxFree  / gratuity : 0.5;
  const txPct   = gratuity > 0 ? taxable  / gratuity : 0.5;
  const tfDash  = Math.max(tfPct * circ - gap, 0);
  const txDash  = Math.max(txPct * circ - gap, 0);

  const css = `
    .grat-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 5px; border-radius: 3px; outline: none; cursor: pointer; }
    .grat-slider::-webkit-slider-thumb { -webkit-appearance: none; width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2.5px solid #115e59; cursor: pointer; box-shadow: 0 1px 6px rgba(0,0,0,0.15); }
    .grat-slider::-moz-range-thumb { width: 20px; height: 20px; border-radius: 50%; background: #fff; border: 2.5px solid #115e59; cursor: pointer; }
    .no-spin::-webkit-outer-spin-button, .no-spin::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    .no-spin { -moz-appearance: textfield; }
    .grat-card { padding: 36px; }
    .grat-seo  { background-color: #ffffff; padding: 48px 450px 64px 100px; }
    .grat-two-col { display: flex; gap: 24px; align-items: flex-start; }
    .grat-chart-row { display: flex; align-items: center; gap: 36px; }
    @media (min-width: 640px) and (max-width: 1023px) {
      .hero-section { padding: 32px 40px 80px 40px !important; }
      .grat-seo { padding: 48px 40px 64px 40px !important; }
      .grat-card { padding: 28px !important; }
    }
    @media (max-width: 639px) {
      .grat-two-col { flex-direction: column !important; }
      .grat-chart-row { flex-direction: column !important; align-items: stretch !important; }
      .grat-card { padding: 20px !important; }
      .hero-section { padding: 32px 20px 60px 20px !important; }
      .grat-seo { padding: 32px 20px 48px 20px !important; }
    }
  `;

  const faqs = [
    { q: 'Who is eligible for gratuity?', a: 'Any employee who has completed at least 5 years of continuous service with the same employer is eligible for gratuity on resignation, retirement, or termination. The 5-year requirement is waived in case of death or disability.' },
    { q: 'What is the difference between "covered" and "not covered" under the Gratuity Act?', a: 'Companies with 10 or more employees are covered under the Payment of Gratuity Act, 1972. For these employees, the formula uses 26 working days. For companies not covered (fewer than 10 employees), some employers still pay gratuity using 30 working days as the base.' },
    { q: 'What counts as salary for gratuity calculation?', a: 'For the purpose of gratuity calculation, "salary" means the last drawn Basic Pay plus Dearness Allowance (DA). Other allowances like HRA, conveyance, or special allowance are not included.' },
    { q: 'What is the tax-free limit on gratuity?', a: 'For private-sector employees, gratuity up to ₹20 lakh is tax-exempt. Government employees enjoy full exemption with no upper limit. Any gratuity received above ₹20 lakh by a private employee is added to taxable income.' },
    { q: 'Does a year with more than 6 months count as a full year?', a: 'Yes. If the last year of service includes more than 6 months, it is rounded up to a full year for gratuity calculation. For example, 10 years and 7 months counts as 11 years.' },
    { q: 'When must the employer pay gratuity?', a: 'Under the Payment of Gratuity Act, the employer must pay gratuity within 30 days of it becoming due. A delay beyond 30 days attracts simple interest at the rate specified by the government.' },
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <link href="https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&family=Onest:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
      <style>{css}</style>
      <Navbar background="#1a4d3e" />

      {/* Hero */}
      <div className="hero-section" style={{ background: 'linear-gradient(180deg, #1a4d3e 0%, #2d6b5a 30%, #4a9d7f 60%, #7ec9a8 85%, #ffffff 100%)', padding: '32px 100px 80px 100px' }}>

        {/* Breadcrumb */}
        <div style={{ marginBottom: '18px' }}>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.75)', textDecoration: 'none', fontWeight: 400 }}>Home</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <Link to="/" style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.55)', textDecoration: 'none', fontWeight: 400 }}>Calculators</Link>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)', margin: '0 6px' }}>/</span>
          <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>Gratuity Calculator</span>
        </div>

        {/* Title + Share */}
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <h1 style={{ fontFamily: 'Instrument Serif, serif', fontWeight: 400, fontSize: '62px', color: '#ffffff', margin: 0, lineHeight: 1 }}>Gratuity Calculator</h1>
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowShareMenu(!showShareMenu)}
              style={{ fontFamily: 'Onest, sans-serif', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', color: '#ffffff', fontWeight: 600, fontSize: '14px', paddingBottom: '8px' }}
            >
              <Share2 size={16} /> SHARE
            </button>
            {showShareMenu && (
              <div style={{ position: 'absolute', right: 0, top: '36px', background: '#fff', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', border: '1px solid #f3f4f6', padding: '12px', zIndex: 50, minWidth: '220px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontFamily: 'Onest', fontWeight: 600, fontSize: '13px', color: '#111827' }}>Share via</span>
                  <button onClick={() => setShowShareMenu(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={16} /></button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around' }}>
                  {[
                    { id: 'whatsapp', label: 'WhatsApp', bg: '#25D366', icon: <svg width="20" height="20" fill="white" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> },
                    { id: 'twitter',  label: 'X',        bg: '#000',     icon: <svg width="16" height="16" fill="white" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                    { id: 'copy',     label: copied ? 'Copied!' : 'Copy', bg: '#e5e7eb', icon: copied ? <Check size={18} color="#16a34a" /> : <Copy size={18} color="#374151" /> },
                  ].map(({ id, label, bg, icon }) => (
                    <button key={id} onClick={() => handleShare(id)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', padding: '8px', borderRadius: '8px', background: 'none', border: 'none', cursor: 'pointer' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
                      <span style={{ fontFamily: 'Onest', fontSize: '11px', color: '#6b7280' }}>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Two-col layout */}
        <div className="grat-two-col">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="grat-card" style={{ backgroundColor: '#ffffff', borderRadius: '24px', boxShadow: '0 8px 48px rgba(0,0,0,0.18)' }}>

              {/* Employment type tabs */}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '32px', flexWrap: 'wrap' }}>
                {[
                  { label: 'Covered under Gratuity Act', value: true },
                  { label: 'Not covered under Act', value: false },
                ].map(({ label, value }) => (
                  <button
                    key={String(value)}
                    onClick={() => setCoveredAct(value)}
                    style={{
                      fontFamily: 'Onest, sans-serif', fontWeight: 600, fontSize: '14px',
                      padding: '9px 22px', borderRadius: '50px', cursor: 'pointer',
                      backgroundColor: coveredAct === value ? '#115e59' : '#f3f4f6',
                      color: coveredAct === value ? '#ffffff' : '#6b7280',
                      border: coveredAct === value ? 'none' : '1.5px solid #e5e7eb',
                      transition: 'all 0.18s ease',
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '12px', color: '#9ca3af', marginTop: '-22px', marginBottom: '28px' }}>
                {coveredAct ? 'Formula: (15 × last salary × years) ÷ 26 working days' : 'Formula: (15 × last salary × years) ÷ 30 days'}
              </p>

              {/* Last Drawn Salary */}
              <div style={{ marginBottom: '30px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={labelSt}>Last Drawn Salary <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: 400 }}>— Basic + DA only</span></label>
                  <div style={inputBoxSt}>
                    <span style={suffixSt}>₹</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="no-spin"
                      value={salaryStr}
                      onChange={(e) => setSalaryStr(e.target.value.replace(/[^0-9]/g, ''))}
                      style={{ ...valueSt, width: '90px' }}
                    />
                    <span style={suffixSt}>/mo</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="grat-slider"
                  min={5000} max={500000} step={1000}
                  value={sliderSalary}
                  onChange={(e) => setSalaryStr(e.target.value)}
                  style={{ background: sliderBg(sliderSalary, 5000, 500000) }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', marginBottom: '10px' }}>
                  <span style={minMaxSt}>₹5K</span>
                  <span style={minMaxSt}>₹5L</span>
                </div>
                <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                  {[20000, 50000, 100000, 200000].map((v) => {
                    const active = sliderSalary === v;
                    return (
                      <button key={v} onClick={() => setSalaryStr(String(v))}
                        style={{ fontFamily: 'Onest, sans-serif', fontSize: '12px', fontWeight: 600, padding: '4px 13px', borderRadius: '50px', border: active ? 'none' : '1.5px solid #e5e7eb', cursor: 'pointer', backgroundColor: active ? '#115e59' : '#f9fafb', color: active ? '#fff' : '#6b7280', transition: 'all 0.15s' }}>
                        {v >= 100000 ? `₹${v / 100000}L` : `₹${v / 1000}K`}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Years of Service */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={labelSt}>Years of Service</label>
                  <div style={inputBoxSt}>
                    <input
                      type="text"
                      inputMode="numeric"
                      className="no-spin"
                      value={years}
                      onChange={(e) => { const v = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 1; setYears(Math.min(Math.max(v, 1), 50)); }}
                      style={{ ...valueSt, width: '40px' }}
                    />
                    <span style={suffixSt}>Yr</span>
                  </div>
                </div>
                <input
                  type="range"
                  className="grat-slider"
                  min={1} max={50} step={1}
                  value={years}
                  onChange={(e) => setYears(parseInt(e.target.value))}
                  style={{ background: sliderBg(years, 1, 50) }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', marginBottom: '10px' }}>
                  <span style={minMaxSt}>1 Yr</span>
                  <span style={minMaxSt}>50 Yr</span>
                </div>
                <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                  {[5, 10, 15, 20, 25].map((v) => {
                    const active = years === v;
                    return (
                      <button key={v} onClick={() => setYears(v)}
                        style={{ fontFamily: 'Onest, sans-serif', fontSize: '12px', fontWeight: 600, padding: '4px 13px', borderRadius: '50px', border: active ? 'none' : '1.5px solid #e5e7eb', cursor: 'pointer', backgroundColor: active ? '#115e59' : '#f9fafb', color: active ? '#fff' : '#6b7280', transition: 'all 0.15s' }}>
                        {v} Yr
                      </button>
                    );
                  })}
                </div>
                {years < 5 && (
                  <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
                    <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '12px', color: '#92400e', margin: 0 }}>
                      Minimum 5 years of service required for gratuity eligibility (except in case of death or disability).
                    </p>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div style={{ borderTop: '1.5px solid #f3f4f6', margin: '32px 0' }} />

              {/* Chart + Results */}
              <div className="grat-chart-row">
                {/* Donut */}
                <div style={{ flexShrink: 0 }}>
                  <svg viewBox="0 0 200 200" width="180" height="180">
                    <g transform={`rotate(-90, ${CX}, ${CY})`}>
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#f3f4f6" strokeWidth="28" />
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#115e59" strokeWidth="28"
                        strokeDasharray={`${tfDash} ${circ}`} strokeLinecap="butt" />
                      <circle cx={CX} cy={CY} r={R} fill="none" stroke="#4ade80" strokeWidth="28"
                        strokeDasharray={`${txDash} ${circ}`} strokeDashoffset={-(tfDash + gap)} strokeLinecap="butt" />
                    </g>
                    <text x={CX} y={CY - 6} textAnchor="middle" style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', fill: '#9ca3af', fontWeight: 500 }}>Gratuity</text>
                    <text x={CX} y={CY + 14} textAnchor="middle" style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', fill: '#111827', fontWeight: 700 }}>
                      {gratuity >= 10000000 ? `₹${(gratuity / 10000000).toFixed(1)}Cr` : gratuity >= 100000 ? `₹${(gratuity / 100000).toFixed(1)}L` : `₹${(gratuity / 1000).toFixed(0)}K`}
                    </text>
                  </svg>
                </div>

                {/* Result rows */}
                <div style={{ flex: 1 }}>
                  {[
                    { label: 'Tax-Free Gratuity',   value: taxFree,   dot: '#115e59' },
                    { label: 'Taxable Gratuity',     value: taxable,   dot: '#4ade80' },
                  ].map(({ label, value, dot }) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: dot, flexShrink: 0 }} />
                        <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>{label}</span>
                      </div>
                      <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 700, color: '#111827' }}>{fmt(value)}</span>
                    </div>
                  ))}

                  <div style={{ borderTop: '1px solid #f3f4f6', margin: '16px 0' }} />

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#111827', fontWeight: 600 }}>Total Gratuity</span>
                    <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '22px', fontWeight: 800, color: '#115e59', letterSpacing: '-0.02em' }}>{fmtC(gratuity)}</span>
                  </div>

                  <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '11px', color: '#9ca3af', marginTop: '8px', lineHeight: 1.5 }}>
                    {coveredAct ? `(₹${salary.toLocaleString('en-IN')} × 15 × ${years}) ÷ 26` : `(₹${salary.toLocaleString('en-IN')} × 15 × ${years}) ÷ 30`} · ₹20L tax-free limit
                  </p>
                </div>
              </div>

            </div>
          </div>

          <SmartCalculatorsSidebar currentSlug="gratuity" rightOffset="100px" />
        </div>
      </div>

      {/* SEO / FAQ */}
      <div className="grat-seo">
        <div style={{ marginBottom: '48px' }}>
          <h2 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '36px', color: '#1f2937', marginBottom: '16px' }}>
            Gratuity Calculator – Calculate Your Gratuity Amount
          </h2>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '16px', color: '#6b7280', lineHeight: 1.7, marginBottom: '16px' }}>
            The Kapitalised Gratuity Calculator helps you calculate the exact gratuity payable at retirement, resignation, or termination. Enter your last drawn salary (basic + DA) and years of service to instantly see your total gratuity, tax-free portion, and any taxable amount.
          </p>
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} style={{ fontFamily: 'Onest, sans-serif', backgroundColor: '#115e59', color: '#ffffff', padding: '14px 32px', borderRadius: '8px', fontWeight: 600, fontSize: '15px', border: 'none', cursor: 'pointer' }}>
            Calculate Your Gratuity
          </button>
        </div>

        <div style={{ marginBottom: '48px' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '20px', color: '#1f2937', marginBottom: '16px' }}>Gratuity Formula</h3>
          <div style={{ backgroundColor: '#115e59', padding: '24px', borderRadius: '16px', marginBottom: '16px' }}>
            <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '18px', fontWeight: 700, color: '#ffffff', marginBottom: '20px', fontStyle: 'italic' }}>
              Gratuity = (15 × Last Salary × Years of Service) ÷ 26
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                ['15', "Number of days' wages per year of service"],
                ['Last Salary', 'Basic Pay + Dearness Allowance (monthly)'],
                ['Years', 'Completed years of continuous service'],
                ['26', 'Working days per month (Payment of Gratuity Act)'],
              ].map(([sym, desc]) => (
                <div key={sym} style={{ backgroundColor: 'rgba(255,255,255,0.12)', padding: '10px 14px', borderRadius: '8px' }}>
                  <span style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, color: '#4ade80', fontSize: '15px' }}>{sym}</span>
                  <span style={{ fontFamily: 'Onest, sans-serif', color: 'rgba(255,255,255,0.85)', fontSize: '13px', marginLeft: '8px' }}>= {desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '48px', backgroundColor: '#fff7ed', padding: '24px', borderRadius: '16px', border: '1px solid #fed7aa' }}>
          <h3 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '18px', color: '#92400e', marginBottom: '12px', marginTop: 0 }}>Tax-Free Limit</h3>
          <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '15px', color: '#78350f', lineHeight: 1.7, margin: 0 }}>
            For private-sector employees, gratuity up to <strong>₹20 lakh</strong> is completely tax-exempt. Government employees receive full exemption with no cap. Any gratuity above ₹20 lakh for private employees is taxable in the year of receipt.
          </p>
        </div>

        <h2 style={{ fontFamily: 'Onest, sans-serif', fontWeight: 700, fontSize: '24px', color: '#1f2937', marginBottom: '20px' }}>Frequently Asked Questions</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {faqs.map(({ q, a }, i) => (
            <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', background: openFaq === i ? '#f9fafb' : '#fff', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                <span style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>{q}</span>
                <ChevronDown size={16} color="#6b7280" style={{ transform: openFaq === i ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s', flexShrink: 0, marginLeft: '12px' }} />
              </button>
              {openFaq === i && (
                <div style={{ padding: '0 20px 16px 20px', background: '#f9fafb' }}>
                  <p style={{ fontFamily: 'Onest, sans-serif', fontSize: '14px', color: '#6b7280', lineHeight: 1.7, margin: 0 }}>{a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GratuityCalculator;
